/**
 * Enquadramento de imagem "como no Instagram": arrastar para posicionar, zoom pelo controle ou pela roda do mouse.
 * Uso: const r = await crop.open(fileOuDataUrl, { aspect: 1, round: true, initial }); r?.render(256, 256, "image/jpeg", "#fff")
 * Devolve também o original reduzido (`source`) e o enquadramento (`crop`), para "Ajustar" depois sem enviar de novo.
 */
import { useRef, useState } from "react";
import { fitDataUrl, loadImage } from "@/lib/image";
import { Modal } from "./kit";

export type Render = (ow: number, oh: number, type?: "image/png" | "image/jpeg", bg?: string, quality?: number) => string;
/** Enquadramento relativo à largura da moldura. */
export interface Crop { x: number; y: number; w: number }
export interface CropResult { render: Render; source: string; crop: Crop }
interface Opts { aspect: number; round?: boolean; title?: string; hint?: string; start?: "cover" | "fit"; initial?: Crop | null }
interface St { img: HTMLImageElement; opts: Opts; s: number; x: number; y: number; base: number }

const FW = 360; // largura da moldura na tela

export function useCropper() {
  const [st, setSt] = useState<St | null>(null);
  const resolver = useRef<((r: CropResult | null) => void) | null>(null);
  const drag = useRef<{ px: number; py: number; x: number; y: number } | null>(null);

  const open = async (input: File | string, opts: Opts): Promise<CropResult | null> => {
    const img = typeof input === "string" ? await fromUrl(input) : await reduce(await loadImage(input), input.type);
    const w = img.naturalWidth || 512, h = img.naturalHeight || 512;
    const FH = FW / opts.aspect;
    const fit = Math.min(FW / w, FH / h);      // imagem inteira visível
    const cover = Math.max(FW / w, FH / h);    // preenche a moldura
    const i = opts.initial;
    if (i && i.w > 0) { const s0 = (i.w * FW) / w; setSt({ img, opts, s: s0, x: i.x * FW, y: i.y * FW, base: fit }); }
    else { const s = opts.start === "fit" ? fit : cover; setSt({ img, opts, s, x: (FW - w * s) / 2, y: (FH - h * s) / 2, base: fit }); }
    return new Promise((res) => { resolver.current = res; });
  };
  const finish = (r: CropResult | null) => { resolver.current?.(r); resolver.current = null; setSt(null); };

  let el: JSX.Element | null = null;
  if (st) {
    const { img, opts } = st;
    const FH = FW / opts.aspect;
    const w = img.naturalWidth || 512, h = img.naturalHeight || 512;
    const zoom = (ns: number) => {
      const s2 = Math.max(st.base * 0.5, Math.min(st.base * 8, ns));
      const cx = FW / 2, cy = FH / 2;
      setSt({ ...st, s: s2, x: cx - ((cx - st.x) / st.s) * s2, y: cy - ((cy - st.y) / st.s) * s2 });
    };
    const render: Render = (ow, oh, type = "image/png", bg, quality = 0.9) => {
      const c = document.createElement("canvas");
      c.width = ow; c.height = oh;
      const ctx = c.getContext("2d")!;
      if (bg) { ctx.fillStyle = bg; ctx.fillRect(0, 0, ow, oh); }
      const k = ow / FW;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, st.x * k, st.y * k, w * st.s * k, h * st.s * k);
      return c.toDataURL(type, quality);
    };
    el = (
      <Modal title={opts.title ?? "Ajustar imagem"} onClose={() => finish(null)}
        footer={<><button className="btn" onClick={() => finish(null)}>Cancelar</button><button className="btn primary" onClick={() => finish({ render, source: img.src, crop: { x: st.x / FW, y: st.y / FW, w: (w * st.s) / FW } })}>Usar este enquadramento</button></>}>
        <div className="stack" style={{ alignItems: "center" }}>
          <div className={`crop-frame ${opts.round ? "round" : ""}`} style={{ width: FW, height: FH }}
            onPointerDown={(e) => { (e.target as HTMLElement).setPointerCapture(e.pointerId); drag.current = { px: e.clientX, py: e.clientY, x: st.x, y: st.y }; }}
            onPointerMove={(e) => { const d = drag.current; if (d) setSt({ ...st, x: d.x + e.clientX - d.px, y: d.y + e.clientY - d.py }); }}
            onPointerUp={() => { drag.current = null; }}
            onWheel={(e) => zoom(st.s * (e.deltaY < 0 ? 1.08 : 1 / 1.08))}>
            <img src={img.src} alt="" draggable={false} style={{ left: st.x, top: st.y, width: w * st.s, height: h * st.s }} />
          </div>
          <div className="row nowrap" style={{ width: FW, gap: 10 }}>
            <span className="muted small">−</span>
            <input type="range" style={{ flex: 1 }} min={Math.log(st.base * 0.5)} max={Math.log(st.base * 8)} step={0.01} value={Math.log(st.s)} onChange={(e) => zoom(Math.exp(Number(e.target.value)))} />
            <span className="muted small">+</span>
            <button className="link-btn small" onClick={() => setSt({ ...st, s: st.base, x: (FW - w * st.base) / 2, y: (FH - h * st.base) / 2 })}>imagem inteira</button>
          </div>
          <div className="muted small">Arraste para posicionar; use o controle ou a roda do mouse para o zoom. {opts.hint ?? ""}</div>
        </div>
      </Modal>
    );
  }
  return { open, el };
}

function fromUrl(u: string): Promise<HTMLImageElement> {
  return new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = () => rej(new Error("Não foi possível abrir a imagem.")); i.src = u; });
}

/** Original reduzido (até 1400px; PNG mantém transparência), guardado para reajustes. */
async function reduce(img: HTMLImageElement, type: string): Promise<HTMLImageElement> {
  const png = type === "image/png" || type === "image/svg+xml" || type === "image/webp";
  let u = fitDataUrl(img, 1400, 1400, png ? "image/png" : "image/jpeg", png ? undefined : "#ffffff", 0.88);
  if (u.length > 1_500_000) u = fitDataUrl(img, 900, 900, png ? "image/png" : "image/jpeg", png ? undefined : "#ffffff", 0.85);
  if (u.length > 1_500_000) u = fitDataUrl(img, 900, 900, "image/jpeg", "#ffffff", 0.8);
  return fromUrl(u);
}
